import { apiFetch } from '@/lib/api'

export interface Bucket {
  t: string
  v: number
}

export interface AnalyticsResponse {
  generated_at: string
  people: {
    total: number
    active: number
    bots: number
    verified_bots: number
    today: number
    this_week: number
    this_month: number
  }
  content: {
    posts_today: number
    posts_total: number
    posts_visible: number
    stories_today: number
    quickies_today: number
    comments_today: number
    businesses_total: number
  }
  timeseries: {
    signups_30d: Bucket[]
    posts_30d: Bucket[]
    comments_30d: Bucket[]
  }
  top_creators: Array<{
    id: number
    username: string | null
    posts_30d: number
  }>
  top_cities: Array<{ city: string; users: number }>
  funnel: { signups_30d: number; with_first_post: number; retained_7d: number }
}

export interface AIResponse {
  generated_at: string
  cost: { today: number; week: number; month: number; all_time: number }
  per_agent_today: Array<{
    agent: string
    runs: number
    cost_usd: number
    tokens_in: number
    tokens_out: number
  }>
  ceo: {
    conversations_total: number
    conversations_active_today: number
    messages_today: number
    tool_calls_today: number
    tool_calls_success_today: number
  }
  cache_stats: {
    input: number
    cache_read: number
    cache_write: number
    hit_rate: number
  }
  bots: {
    actions_today: number
    actions_last_hour: number
    actions_30d: Bucket[]
  }
  run_status_24h: Array<{ status: string; count: number }>
  cost_30d: Bucket[]
}

export interface ScrapersResponse {
  generated_at: string
  sources: Array<{
    key: string
    label: string
    available: boolean
    total?: number
    today?: number
    running?: number
    failed_24h?: number
    last_run_at?: string | null
    last_run_status?: string | null
  }>
  derived_totals: Record<string, number>
}

export interface FinanceResponse {
  generated_at: string
  gmv: { today: number; week: number; month: number }
  order_count: { today: number; week: number; month: number; all_time: number }
  aov: number
  wallet_totals: {
    available: number
    pending: number
    earned: number
    withdrawn: number
    users: number
  }
  payouts: { by_status: Record<string, { count: number; amount: number }> }
  bookings: {
    reservations_today: number
    reservations_week: number
    service_bookings_today: number
    space_bookings_today: number
  }
  top_sellers: Array<{
    business_id: number | null
    business_name: string | null
    orders: number
    revenue: number
  }>
  orders_30d: Bucket[]
}

export interface SystemResponse {
  generated_at: string
  queue_depth: number
  redis_ok: boolean
  redis_info: { used_memory_human?: string; maxmemory_human?: string }
  beat_schedule: Array<{
    key: string
    task: string | null
    schedule_seconds: number | null
  }>
  last_metrics_refresh_at: string | null
  audit_log_24h: number
}

export const fetchDashAnalytics = () =>
  apiFetch<AnalyticsResponse>('/api/admin/dashboard/analytics/')
export const fetchDashAI = () =>
  apiFetch<AIResponse>('/api/admin/dashboard/ai/')
export const fetchDashScrapers = () =>
  apiFetch<ScrapersResponse>('/api/admin/dashboard/scrapers/')
export const fetchDashFinance = () =>
  apiFetch<FinanceResponse>('/api/admin/dashboard/finance/')
export const fetchDashSystem = () =>
  apiFetch<SystemResponse>('/api/admin/dashboard/system/')
